import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Check, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/app/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

interface SenderProfile {
  email?: string;
  first_name?: string;
  last_name?: string;
}

interface PendingInvite {
  id: string;
  sender_id: string;
  recipient_email: string;
  relationship: string;
  status: string;
  created_at: string;
  profiles?: SenderProfile;
  sender_name?: string;
  sender_email?: string;
}

const PendingInvites: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchPendingInvites = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      try {
        console.log("Fetching pending invites for email:", user.email);
        
        // First, fetch the invites
        const { data: invitesData, error: invitesError } = await supabase
          .from('ft_friend_invites')
          .select('*')
          .eq('recipient_email', user.email)
          .eq('status', 'pending');
          
        if (invitesError) {
          console.error("Error fetching pending invites:", invitesError);
          setLoading(false);
          return;
        }
        
        console.log("Found pending invites:", invitesData);
        
        if (invitesData && invitesData.length > 0) {
          // Fetch sender profiles separately
          const transformedInvites = await Promise.all(invitesData.map(async (invite) => {
            // Get profile info for the sender
            const { data: profileData } = await supabase
              .from('ft_profiles')
              .select('*')
              .eq('id', invite.sender_id)
              .single();
              
            return {
              ...invite,
              sender_name: profileData?.first_name 
                ? `${profileData.first_name} ${profileData.last_name || ''}`.trim()
                : profileData?.email || 'Unknown User',
              sender_email: profileData?.email
            };
          }));
          
          setPendingInvites(transformedInvites);
        } else {
          setPendingInvites([]);
        }
      } catch (error) {
        console.error("Error in fetchPendingInvites:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPendingInvites();
    
    // Set up a subscription to listen for new invites
    const invitesSubscription = supabase
      .channel('ft_friend_invites_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'ft_friend_invites',
          filter: `recipient_email=eq.${user?.email}`
        }, 
        (payload) => {
          console.log('Invite change received!', payload);
          fetchPendingInvites();
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(invitesSubscription);
    };
  }, [user]);
  
  const handleAcceptInvite = async (inviteId: string, relationship: string, senderId: string) => {
    if (!user) return;
    
    try {
      // Update invite status
      const { error: updateError } = await supabase
        .from('ft_friend_invites')
        .update({ status: 'accepted' })
        .eq('id', inviteId);
        
      if (updateError) {
        throw updateError;
      }
      
      // Add to contacts table
      const invite = pendingInvites.find(inv => inv.id === inviteId);
      if (invite) {
        // Add sender to user's contacts
        const { error: contactError } = await supabase
          .from('ft_contacts')
          .insert({
            user_id: user.id,
            contact_id: senderId,
            email: invite.sender_email || '',
            name: invite.sender_name || 'Unknown User',
            relationship: relationship
          });
          
        if (contactError) {
          console.error("Error adding contact:", contactError);
        }
        
        // Add user to sender's contacts
        const { error: reverseContactError } = await supabase
          .from('ft_contacts')
          .insert({
            user_id: senderId,
            contact_id: user.id,
            email: user.email || '',
            name: user.user_metadata?.name || user.email || 'Unknown User',
            relationship: relationship
          });
          
        if (reverseContactError) {
          console.error("Error adding reverse contact:", reverseContactError);
        }
        
        // Create a notification for the inviter
        await supabase
          .from('ft_notifications')
          .insert({
            user_id: invite.sender_id,
            message: `${user.email} accepted your invitation to connect as ${relationship}`,
            type: 'invite_accepted',
            is_read: false,
            status: 'unread',
            action_url: '/friends-and-family'
          });
      }
      
      // Update UI
      setPendingInvites(pendingInvites.filter(invite => invite.id !== inviteId));
      
      toast({
        title: "Invitation accepted",
        description: "You are now connected",
      });
    } catch (error) {
      console.error("Error accepting invite:", error);
      toast({
        title: "Error",
        description: "Failed to accept invitation. Please try again.",
        variant: "destructive"
      });
    }
  };
  
  const handleRejectInvite = async (inviteId: string) => {
    if (!user) return;
    
    try {
      // Update invite status
      const { error: updateError } = await supabase
        .from('ft_friend_invites')
        .update({ status: 'rejected' })
        .eq('id', inviteId);
        
      if (updateError) {
        throw updateError;
      }
      
      // Create a notification for the inviter
      const invite = pendingInvites.find(inv => inv.id === inviteId);
      if (invite) {
        await supabase
          .from('ft_notifications')
          .insert({
            user_id: invite.sender_id,
            message: `${user.email} rejected your invitation to connect`,
            type: 'invite_rejected',
            is_read: false,
            status: 'unread',
            action_url: '/friends-and-family'
          });
      }
      
      // Update UI
      setPendingInvites(pendingInvites.filter(invite => invite.id !== inviteId));
      
      toast({
        title: "Invitation rejected",
        description: "The invitation has been declined",
      });
    } catch (error) {
      console.error("Error rejecting invite:", error);
      toast({
        title: "Error",
        description: "Failed to reject invitation. Please try again.",
        variant: "destructive"
      });
    }
  };
  
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-black">Pending Invitations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (pendingInvites.length === 0) {
    return null; // Don't show the card if there are no pending invites
  }
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-black">
          Pending Invitations
          <Badge className="ml-2 bg-blue-600">{pendingInvites.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {pendingInvites.map(invite => (
            <Card key={invite.id} className="overflow-hidden">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <Avatar>
                      <AvatarFallback>
                        {invite.sender_name?.[0] || invite.sender_id?.[0] || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-black">{invite.sender_name || 'Unknown User'}</p>
                      <p className="text-sm text-gray-500">{invite.sender_email || ''}</p>
                      <Badge 
                        variant={invite.relationship === "family" ? "destructive" : "default"}
                        className="mt-1"
                      >
                        <span className="text-black">
                          Wants to be your {invite.relationship}
                        </span>
                      </Badge>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => handleAcceptInvite(invite.id, invite.relationship, invite.sender_id)}
                      className="border-green-500 hover:bg-green-50"
                    >
                      <Check className="h-4 w-4 text-green-500" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => handleRejectInvite(invite.id)}
                      className="border-red-500 hover:bg-red-50"
                    >
                      <X className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default PendingInvites;
